import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { getCryptoIconUrl, getCryptoIconFallback } from "@/lib/cryptoIcons";

const DEFAULT_MULTIPLIERS = [0.01, 0.25, 0.5, 0.75, 1, 1.5, 2, 5, 10, 25, 50, 100, 500, 1000];

// Base design dimensions
const BASE_TICKET_W = 270;
const BASE_TICKET_H = 100;
const BASE_GAP = 12;

// Wavy ticket with EXACTLY 7 scalloped edges on each side
// Using quadratic curves for smooth, deep waves that scale perfectly
const TICKET_PATH = `
  M 20,0 
  H 250 
  Q 270,0 270,10
  Q 258,15.7 270,21.4
  Q 258,27.1 270,32.8
  Q 258,38.5 270,44.2
  Q 258,50.0 270,55.7
  Q 258,61.4 270,67.1
  Q 258,72.8 270,78.5
  Q 258,84.2 270,90.0
  Q 270,100 250,100
  H 20
  Q 0,100 0,90
  Q 12,84.2 0,78.5
  Q 12,72.8 0,67.1
  Q 12,61.4 0,55.7
  Q 12,50.0 0,44.2
  Q 12,38.5 0,32.8
  Q 12,27.1 0,21.4
  Q 12,15.7 0,10
  Q 0,0 20,0 Z
`.replace(/\s+/g, ' ').trim();

const TicketSVG = ({ color, borderColor, children, className, width, height, id }) => {
    const glossId = `gloss-${id}`;

    return (
        <div className={cn("relative flex-shrink-0 drop-shadow-[0_4px_12px_rgba(0,0,0,0.25)] overflow-visible", className)} style={{ width, height }}>
            <svg viewBox="0 0 270 100" className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none">
                <defs>
                    <linearGradient id={glossId} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="white" stopOpacity="0.3" />
                        <stop offset="30%" stopColor="white" stopOpacity="0.1" />
                        <stop offset="100%" stopColor="black" stopOpacity="0.3" />
                    </linearGradient>
                </defs>

                {/* Dark Bevel Bottom Shadow */}
                <path
                    d={TICKET_PATH}
                    fill="black"
                    fillOpacity="0.15"
                    transform="translate(0, 3)"
                />

                {/* Main Body */}
                <path d={TICKET_PATH} fill={color} />
                <path d={TICKET_PATH} fill={`url(#${glossId})`} />

                {/* Outline */}
                <path
                    d={TICKET_PATH}
                    fill="none"
                    stroke={borderColor}
                    strokeWidth="3"
                    vectorEffect="non-scaling-stroke"
                />

                {/* Inner highlight core (simplified version for top-inner border) */}
                <path
                    d="M 22,3 H 248 Q 267,3 267,13"
                    fill="none"
                    stroke="white"
                    strokeOpacity="0.25"
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                />
            </svg>
            <div className="relative z-10 w-full h-full">
                {children}
            </div>
        </div>
    );
};


export const LuckyTickets = ({ targetMultiplier, onSpinComplete, spinId, scale = 1, multipliers = DEFAULT_MULTIPLIERS }) => {
    const [tickets, setTickets] = useState([]);
    const [offset, setOffset] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const containerRef = useRef(null);
    const onCompleteRef = useRef(onSpinComplete);
    const ticketMultipliers = Array.isArray(multipliers) && multipliers.length > 0
        ? multipliers
        : DEFAULT_MULTIPLIERS;

    useEffect(() => { onCompleteRef.current = onSpinComplete; }, [onSpinComplete]);
    // Base design dimensions
    const BASE_TICKET_W = 270;
    const BASE_TICKET_H = 100;
    const BASE_GAP = 12;

    // Derived dimensions from prop
    const ticketH = BASE_TICKET_H * scale;

    const gap = BASE_GAP * scale;
    const fullTicketHeight = ticketH + gap;

    useEffect(() => {
        const initial = Array.from({ length: 60 }, () =>
            ticketMultipliers[Math.floor(Math.random() * ticketMultipliers.length)]
        );
        setTickets(initial);
    }, [spinId, ticketMultipliers]); // Reset on every spinId change to ensure fresh DOM state

    useEffect(() => {
        if (spinId > 0 && targetMultiplier !== null) {
            setIsAnimating(false);
            setOffset(0);

            const spinLength = 60 + Math.floor(Math.random() * 10);
            const winnerIndex = spinLength - 8;

            const newTickets = Array.from({ length: spinLength }, (_, i) => {
                if (i === winnerIndex) return targetMultiplier;
                return ticketMultipliers[Math.floor(Math.random() * ticketMultipliers.length)];
            });
            setTickets(newTickets);

            const triggerTimer = setTimeout(() => {
                const containerHeight = containerRef.current?.offsetHeight || 0;
                const centerPos = (containerHeight / 2) - (ticketH / 2);

                const randomShift = (Math.random() - 0.5) * 60 * scale;
                const finalOffset = -(winnerIndex * fullTicketHeight) + centerPos + randomShift;

                setIsAnimating(true);
                setOffset(finalOffset);
            }, 100); // Increased from 60ms to 100ms for more reliable reset-then-animate cycle

            const completeTimer = setTimeout(() => {
                if (onCompleteRef.current) onCompleteRef.current();
            }, 4660);

            return () => {
                clearTimeout(triggerTimer);
                clearTimeout(completeTimer);
            };
        }
    }, [spinId, targetMultiplier, scale, ticketH, fullTicketHeight, ticketMultipliers]);

    const getColors = (mult) => {
        if (mult >= 500) return { bg: "#ffffff", border: "#b9f2ff", text: "text-[#1a3a3a] dark:text-black dark:font-black", iconBg: "bg-[#26A17B]", jackpot: true };
        if (mult >= 50) return { bg: "#f1c40f", border: "#f39c12", text: "text-[#4a3a0e]", iconBg: "bg-black/10" };
        if (mult >= 5) return { bg: "#e91e63", border: "#c2185b", text: "text-white", iconBg: "bg-[#009688]" };
        return { bg: "#00bcd4", border: "#0097a7", text: "text-[#0e3a41]", iconBg: "bg-white/20" };
    };

    return (
        <div className="w-full h-full relative flex flex-col pt-0 bg-transparent" ref={containerRef}>


            <div
                className="flex flex-col items-center w-full relative"
                style={{
                    transform: `translateY(${offset}px)`,
                    transition: isAnimating ? "transform 4.5s cubic-bezier(0.1, 0.5, 0, 1)" : "transform 0.1s ease-out",
                    gap: `${gap}px`,
                    willChange: "transform"
                }}
            >
                {tickets.map((mult, i) => {
                    const { bg, border, text, iconBg, jackpot } = getColors(mult);
                    return (
                        <TicketSVG
                            key={`ticket-${spinId}-${i}`} // Include spinId to force re-render/reset
                            id={`ticket-${spinId}-${i}`}
                            color={bg}
                            borderColor={border}
                            className={cn(text, jackpot && "jackpot-shine")}
                            width={BASE_TICKET_W * scale}
                            height={ticketH}
                        >
                            <div
                                style={{
                                    transform: `scale(${scale})`,
                                    transformOrigin: 'center',
                                    width: BASE_TICKET_W,
                                    height: BASE_TICKET_H,
                                    position: 'relative'
                                }}
                            >
                                {/* Layout shifted to match screenshot: Icon far left, Number far right */}
                                <div className={cn("absolute left-[12%] top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center overflow-hidden z-20 shadow-inner", iconBg)}>
                                    <img
                                        src={getCryptoIconUrl("USDT")}
                                        alt="USDT"
                                        className="w-full h-full object-cover p-1.5"
                                        loading="lazy"
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = getCryptoIconFallback("USDT");
                                        }}
                                    />
                                </div>

                                <div className="absolute right-[12%] inset-y-0 flex flex-col items-center justify-center z-10">
                                    <div className="text-[64px] leading-[0.9] font-black tracking-tighter drop-shadow-md">
                                        {mult}
                                    </div>
                                    <div className="text-[15px] font-black opacity-90 uppercase tracking-[0.2em] mt-1 pr-1">
                                        Tether
                                    </div>
                                </div>
                            </div>
                        </TicketSVG>
                    );
                })}
            </div>

            <style jsx>{`
                .jackpot-shine {
                    position: relative;
                    overflow: hidden;
                    animation: jackpot-pulse 2s infinite ease-in-out;
                }
                .jackpot-shine::after {
                    content: "";
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: linear-gradient(
                        45deg,
                        transparent 45%,
                        rgba(255, 255, 255, 0.8) 50%,
                        transparent 55%
                    );
                    animation: shine 3s infinite;
                }
                @keyframes shine {
                    0% { transform: translate(-30%, -30%) rotate(0deg); }
                    100% { transform: translate(30%, 30%) rotate(0deg); }
                }
                @keyframes jackpot-pulse {
                    0%, 100% { filter: drop-shadow(0 0 10px rgba(185, 242, 255, 0.5)); }
                    50% { filter: drop-shadow(0 0 25px rgba(185, 242, 255, 0.9)); }
                }
            `}</style>
        </div>
    );
};

export default LuckyTickets;
